<template>
  <v-row>
    <v-col cols="12" md="6">
      <v-card flat outlined class="rounded mb-3">
        <div class="section-head">Request</div>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="4">
              <v-select
                v-model="state.method"
                :items="['POST', 'GET', 'PUT', 'PATCH', 'DELETE']"
                label="Method"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col cols="8">
              <v-text-field
                v-model="state.url"
                label="URL"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
          </v-row>

          <v-text-field
            v-model="state.apiKey"
            label="API Key"
            placeholder="Sent as: Authorization: Api-Key <value>"
            outlined
            dense
            class="mt-4"
            hide-details="auto"
            :append-icon="showApiKey ? 'mdi-eye-off' : 'mdi-eye'"
            :type="showApiKey ? 'text' : 'password'"
            @click:append="showApiKey = !showApiKey"
          />

          <div class="d-flex align-center mt-4 mb-1">
            <span class="field-label">Payload (JSON)</span>
            <v-spacer />
            <v-btn text x-small color="primary" @click="formatPayload">
              <v-icon x-small class="mr-1">mdi-auto-fix</v-icon>
              Format
            </v-btn>
          </div>
          <v-textarea
            v-model="state.payloadText"
            outlined
            dense
            hide-details="auto"
            rows="14"
            class="payload-editor"
            spellcheck="false"
          />
          <div v-if="state.payloadError" class="error--text caption mt-1">
            {{ state.payloadError }}
          </div>

          <v-btn
            color="primary"
            depressed
            class="mt-4"
            :loading="state.sending"
            @click="sendRequest"
          >
            <v-icon left small>mdi-send</v-icon>
            Send Request
          </v-btn>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" md="6">
      <v-card flat outlined class="rounded mb-3" height="100%">
        <div class="section-head">
          Response
          <v-spacer />
          <v-chip
            v-if="state.response.status !== null"
            x-small
            label
            :color="statusColor"
            text-color="white"
            class="font-weight-bold"
          >
            {{ state.response.status }}
          </v-chip>
          <span v-if="state.response.durationMs !== null" class="section-head__meta ml-2">
            {{ state.response.durationMs }} ms
          </span>
        </div>
        <v-card-text class="pt-4">
          <div v-if="!state.response.body && state.response.status === null" class="empty-state">
            Send a request to see the response here.
          </div>
          <pre v-else class="response-body">{{ state.response.body }}</pre>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from "axios";

export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showApiKey: false,
    };
  },
  computed: {
    state() {
      return this.value;
    },
    statusColor() {
      const status = this.state.response.status;
      if (status === null) return "grey";
      if (status >= 200 && status < 300) return "green darken-1";
      if (status >= 400 && status < 500) return "orange darken-2";
      if (status >= 500) return "red darken-2";
      return "grey";
    },
  },
  methods: {
    formatPayload() {
      try {
        const parsed = JSON.parse(this.state.payloadText);
        this.state.payloadText = JSON.stringify(parsed, null, 2);
        this.state.payloadError = "";
      } catch (e) {
        this.state.payloadError = `Invalid JSON: ${e.message}`;
      }
    },
    buildFullUrl() {
      const url = this.state.url;
      if (/^https?:\/\//i.test(url)) return url;
      const base = (process.env.VUE_APP_BASE_URL || "").replace(/\/$/, "");
      const path = url.startsWith("/") ? url : `/${url}`;
      return `${base}${path}`;
    },
    async sendRequest() {
      const method = this.state.method.toUpperCase();
      const hasBody = method !== "GET" && method !== "DELETE";
      let payload;

      if (hasBody) {
        try {
          payload = this.state.payloadText.trim()
            ? JSON.parse(this.state.payloadText)
            : undefined;
          this.state.payloadError = "";
        } catch (e) {
          this.state.payloadError = `Invalid JSON: ${e.message}`;
          return;
        }
      }

      if (!this.state.url.trim()) {
        this.$bus.$emit("showToastMessage", {
          message: "URL is required.",
          color: "error",
        });
        return;
      }

      this.state.sending = true;
      this.state.response = { status: null, body: "", durationMs: null };
      const startedAt = performance.now();

      const headers = {};
      if (this.state.apiKey.trim()) {
        headers.Authorization = `Api-Key ${this.state.apiKey.trim()}`;
      }

      try {
        const res = await axios({
          method,
          url: this.buildFullUrl(),
          data: hasBody ? payload : undefined,
          headers,
          validateStatus: () => true,
        });
        this.state.response = {
          status: res.status,
          body: JSON.stringify(res.data, null, 2),
          durationMs: Math.round(performance.now() - startedAt),
        };
      } catch (err) {
        this.state.response = {
          status: err.response ? err.response.status : "ERR",
          body: err.response
            ? JSON.stringify(err.response.data, null, 2)
            : String(err.message || err),
          durationMs: Math.round(performance.now() - startedAt),
        };
      } finally {
        this.state.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.section-head {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.section-head__meta {
  font-size: 11px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.45);
}
.field-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
}
.payload-editor >>> textarea {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
}
.response-body {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 500px;
  overflow-y: auto;
  margin: 0;
}
.empty-state {
  color: rgba(0, 0, 0, 0.4);
  font-size: 13px;
  padding: 24px 0;
  text-align: center;
}
</style>
